/**
 * Shared utilities for {@link PartnerRow}, {@link PartnerCard} and {@link PartnersDataTable}.
 */
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import {
  PARTNER_ACTION_IDS,
  PARTNER_FILTER_IDS,
  PARTNERS_MENU_ELEMENT_ID,
  type PartnerFilters,
} from '@/components/partners/partner-configs';
import type { ListPartnersParams } from '@/types/partner.types';

/** Formats a city line from zip code + city name. */
export function formatCityLine(
  zipCode: string | null | undefined,
  city: string | null | undefined,
): string | undefined {
  if (!city) return undefined;
  return [zipCode, city].filter(Boolean).join(' ');
}

/** Strips the protocol prefix from a URL for display. */
export function stripProtocol(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

/** Formats a date string in French short format (dd/mm/yyyy). */
export function formatPartnerDate(dateStr: string | null | undefined): string | undefined {
  if (!dateStr) return undefined;
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** RBAC flags for partner list actions and filters. */
export function usePartnerPageActions() {
  const { elements } = useInterfaceElements(PARTNERS_MENU_ELEMENT_ID);

  const find = (elementId: string) => elements.some((el) => el.elementId === elementId);

  return {
    canCreatePartner: find(PARTNER_ACTION_IDS.create),
    canViewPartner: find(PARTNER_ACTION_IDS.view),
    canEditPartner: find(PARTNER_ACTION_IDS.edit),
    canDeletePartner: find(PARTNER_ACTION_IDS.delete),
    canExportPartners: find(PARTNER_ACTION_IDS.exportExcel),
    canUsePartnerSearchFilter: find(PARTNER_FILTER_IDS.search),
    canUsePartnerCityFilter: find(PARTNER_FILTER_IDS.city),
  };
}

/** Map UI filters + pagination to partner list params. */
export function toPartnerListParams(
  filters: PartnerFilters,
  page?: number,
  pageSize?: number,
): ListPartnersParams {
  return {
    search: filters.search,
    city: filters.city,
    page,
    pageSize,
  };
}

/** True when search or city is set. */
export function hasPartnerActiveFilters(filters: PartnerFilters): boolean {
  return !!(filters.search || filters.city);
}
